import React, { useEffect, useState, useRef } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Database, Play, RefreshCw, Trash2, Activity, AlertTriangle } from 'lucide-react';

const MALL_OPTIONS = [
    { id: 1, name: 'Malad' },
    { id: 2, name: 'Vashi' },
    { id: 3, name: 'Hyderabad → Cyberabad' },
    { id: 4, name: 'Vadodara' },
];

export default function AdminCatalog() {
    const [status, setStatus] = useState(null);
    const [malls, setMalls] = useState([1, 2, 3, 4]);
    const [perCat, setPerCat] = useState(40);
    const [busy, setBusy] = useState(false);
    const poller = useRef(null);

    const load = () => api.get('/admin/catalog/scrape/status').then(({ data }) => setStatus(data));

    useEffect(() => {
        load();
        poller.current = setInterval(load, 4000);
        return () => clearInterval(poller.current);
    }, []);

    const startScrape = async () => {
        if (status?.running) { toast.info('Scrape already running'); return; }
        setBusy(true);
        try {
            const { data } = await api.post('/admin/catalog/scrape/start', {
                mall_ids: malls, per_category: perCat, scroll_per_category: 3,
            });
            toast.success(`Scrape started — run ${data.run_id?.slice(0, 8)}`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to start scrape');
        } finally { setBusy(false); }
    };

    const clearCatalog = async () => {
        if (!window.confirm('Delete the entire scraped catalog (products, categories, brands, banners)? This cannot be undone.')) return;
        setBusy(true);
        try {
            const { data } = await api.post('/admin/catalog/clear');
            toast.success(`Cleared ${data.products_deleted} products, ${data.brands_deleted} brands`);
            load();
        } catch (e) {
            toast.error('Failed to clear');
        } finally { setBusy(false); }
    };

    const toggleMall = (id) => {
        setMalls((m) => m.includes(id) ? m.filter((x) => x !== id) : [...m, id]);
    };

    const counts = status?.counts || {};
    const lastRun = status?.runs?.[0];

    return (
        <AdminLayout>
            <div className="p-10" data-testid="admin-catalog-page">
                <div className="mb-10 flex items-end justify-between gap-4">
                    <div>
                        <div className="overline">Data Pipeline</div>
                        <h1 className="font-serif text-4xl text-inorbit-blue mt-2">Catalog Scraper</h1>
                        <div className="text-sm text-text-secondary mt-1">Mirror inorbitonline.com (Malad / Vashi / Hyderabad / Vadodara) into our database.</div>
                    </div>
                    <button onClick={load} className="btn-outline" data-testid="catalog-refresh"><RefreshCw size={14} /> Refresh</button>
                </div>

                {/* Counts */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="catalog-kpis">
                    <KPI label="Products" value={counts.products?.toLocaleString('en-IN') || 0} icon={Database} />
                    <KPI label="Categories" value={counts.categories || 0} icon={Database} />
                    <KPI label="Brands" value={counts.brands || 0} icon={Database} />
                    <KPI label="Banners" value={counts.banners || 0} icon={Database} />
                </div>

                {/* Scrape controls */}
                <div className="grid lg:grid-cols-2 gap-6 mb-10">
                    <div className="bg-white border border-border-subtle rounded-md p-8" data-testid="scrape-controls">
                        <div className="overline mb-4">Trigger Scrape</div>
                        <h3 className="font-serif text-2xl text-inorbit-blue mb-6">Run pipeline</h3>

                        <div className="mb-5">
                            <label className="overline mb-2 block">Malls</label>
                            <div className="flex gap-2 flex-wrap">
                                {MALL_OPTIONS.map((m) => (
                                    <button
                                        key={m.id}
                                        onClick={() => toggleMall(m.id)}
                                        className={`pill ${malls.includes(m.id) ? 'pill-blue' : 'pill-outline'}`}
                                        data-testid={`mall-toggle-${m.id}`}
                                    >
                                        {m.name}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="mb-6">
                            <label className="overline mb-2 block">Products per category</label>
                            <input type="number" min="8" max="200" step="8" value={perCat} onChange={(e) => setPerCat(parseInt(e.target.value) || 40)} className="input-box w-32" data-testid="per-cat-input" />
                            <div className="text-xs text-text-tertiary mt-1">Higher = more SKUs but slower. ~40 is balanced.</div>
                        </div>

                        <div className="flex gap-3">
                            <button onClick={startScrape} disabled={busy || status?.running || malls.length === 0} className="btn-primary disabled:opacity-50" data-testid="start-scrape-btn">
                                <Play size={14} /> {status?.running ? 'Running…' : 'Start scrape'}
                            </button>
                            <button onClick={clearCatalog} disabled={busy || status?.running} className="btn-outline disabled:opacity-50 text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="clear-catalog-btn">
                                <Trash2 size={14} /> Clear catalog
                            </button>
                        </div>
                    </div>

                    {/* Live progress */}
                    <div className="bg-inorbit-blue text-white rounded-md p-8 relative overflow-hidden" data-testid="scrape-progress">
                        <div className="absolute -top-20 -right-20 w-60 h-60 border border-inorbit-yellow/20 rounded-full" />
                        <div className="relative">
                            <div className="overline text-inorbit-yellow mb-4 flex items-center gap-2">
                                <Activity size={14} />
                                Live Status
                            </div>
                            {!lastRun ? (
                                <div className="text-white/70 text-sm">No scrape runs yet. Trigger one to get started.</div>
                            ) : (
                                <>
                                    <div className="mb-4">
                                        <span className={`pill ${lastRun.status === 'running' ? 'pill-yellow' : lastRun.status === 'completed' ? 'pill-blue' : 'pill-outline'}`}>
                                            {lastRun.status}
                                        </span>
                                        {lastRun.status === 'running' && <span className="ml-3 text-xs text-inorbit-yellow animate-pulse">● live</span>}
                                    </div>
                                    <div className="space-y-2 text-sm">
                                        <Row k="Currently" v={lastRun.current || '—'} />
                                        <Row k="Categories processed" v={lastRun.categories_processed || 0} />
                                        <Row k="Inserted" v={lastRun.products_inserted || 0} />
                                        <Row k="Updated" v={lastRun.products_updated || 0} />
                                        <Row k="Brands discovered" v={lastRun.brands_discovered || 0} />
                                        <Row k="Started" v={lastRun.started_at ? new Date(lastRun.started_at).toLocaleTimeString('en-IN') : '—'} />
                                        {lastRun.finished_at && <Row k="Finished" v={new Date(lastRun.finished_at).toLocaleTimeString('en-IN')} />}
                                    </div>
                                    {lastRun.errors?.length > 0 && (
                                        <div className="mt-4 p-3 bg-rose-900/30 border border-rose-400/30 rounded text-xs">
                                            <div className="flex items-center gap-2 text-rose-200 mb-1"><AlertTriangle size={12} /> {lastRun.errors.length} error(s)</div>
                                            {lastRun.errors.slice(0, 3).map((er, i) => (
                                                <div key={i} className="text-rose-100/80 font-mono">{JSON.stringify(er).slice(0, 120)}</div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Run history */}
                {status?.runs?.length > 1 && (
                    <div className="bg-white border border-border-subtle rounded-md p-8" data-testid="run-history">
                        <div className="overline mb-4">Run History</div>
                        <table className="w-full text-sm">
                            <thead className="text-[11px] uppercase tracking-widest text-text-tertiary border-b border-border-subtle">
                                <tr><th className="text-left py-3">Run</th><th className="text-left">Status</th><th className="text-right">Cats</th><th className="text-right">Products</th><th className="text-left">Started</th></tr>
                            </thead>
                            <tbody>
                                {status.runs.map((r) => (
                                    <tr key={r.id} className="border-b border-border-subtle">
                                        <td className="py-3 font-mono text-xs text-inorbit-blue">{r.id?.slice(0, 8)}</td>
                                        <td><span className={`pill ${r.status === 'completed' ? 'pill-blue' : r.status === 'running' ? 'pill-yellow' : 'pill-outline'} text-[10px]`}>{r.status}</span></td>
                                        <td className="text-right text-text-secondary">{r.categories_processed}</td>
                                        <td className="text-right text-inorbit-blue font-semibold">{(r.products_inserted || 0) + (r.products_updated || 0)}</td>
                                        <td className="text-text-tertiary text-xs">{r.started_at ? new Date(r.started_at).toLocaleString('en-IN') : '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}

function KPI({ label, value, icon: Icon }) {
    return (
        <div className="bg-white p-6 border border-border-subtle rounded-md">
            <Icon size={18} strokeWidth={1.4} className="text-text-tertiary" />
            <div className="font-serif text-3xl text-inorbit-blue mt-3 break-all">{value}</div>
            <div className="text-[11px] uppercase tracking-widest text-text-tertiary mt-1">{label}</div>
        </div>
    );
}

function Row({ k, v }) {
    return (
        <div className="flex justify-between gap-4">
            <span className="text-white/60 text-xs uppercase tracking-widest">{k}</span>
            <span className="text-white font-medium truncate max-w-[60%]">{v}</span>
        </div>
    );
}
